/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
import { isPlatformServer } from '@angular/common';
import { Component, ContentChild, EventEmitter, Inject, Input, Output, PLATFORM_ID, ViewChild, ViewContainerRef, } from '@angular/core';
import { UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { JssCanActivateRedirectError } from '../services/jss-can-activate-error';
import { DATA_RESOLVER, GUARD_RESOLVER, PLACEHOLDER_HIDDEN_RENDERING_COMPONENT, PLACEHOLDER_MISSING_COMPONENT_COMPONENT, } from '../services/placeholder.token';
import { HIDDEN_RENDERING_NAME } from './hidden-rendering.component';
import { PlaceholderLoadingDirective } from './placeholder-loading.directive';
import { RenderEachDirective } from './render-each.directive';
import { RenderEmptyDirective } from './render-empty.directive';
import { isRawRendering } from './rendering';
import * as i0 from "@angular/core";
import * as i1 from "../services/jss-component-factory.service";
import * as i2 from "@angular/router";
import * as i3 from "@angular/common";
/**
 * @param {ComponentRendering} rendering
 * @param {string} name
 */
function getPlaceholder(rendering, name) {
    if (rendering && rendering.placeholders && Object.keys(rendering.placeholders).length > 0) {
        return rendering.placeholders[name];
    }
    return null;
}
export class PlaceholderComponent {
    differs;
    componentFactory;
    changeDetectorRef;
    elementRef;
    renderer;
    router;
    missingComponentComponent;
    hiddenRenderingComponent;
    guardResolver;
    dataResolver;
    platformId;
    name;
    rendering;
    renderings;
    outputs;
    clientOnly = false;
    loaded = new EventEmitter();
    failed = new EventEmitter();
    renderEachTemplate;
    renderEmptyTemplate;
    placeholderLoading;
    view;
    isLoading = true;
    _inputs;
    _differ;
    _componentRefs = [];
    destroyed = false;
    parentStyleAttribute = '';
    constructor(differs, componentFactory, changeDetectorRef, elementRef, renderer, router, missingComponentComponent, hiddenRenderingComponent, guardResolver, dataResolver, 
    // eslint-disable-next-line @typescript-eslint/ban-types
    platformId) {
        this.differs = differs;
        this.componentFactory = componentFactory;
        this.changeDetectorRef = changeDetectorRef;
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.router = router;
        this.missingComponentComponent = missingComponentComponent;
        this.hiddenRenderingComponent = hiddenRenderingComponent;
        this.guardResolver = guardResolver;
        this.dataResolver = dataResolver;
        this.platformId = platformId;
    }
    set inputs(value) {
        this._inputs = value;
        if (!this._differ && value) {
            this._differ = this.differs.find(value).create();
        }
    }
    ngOnInit() {
        // just to ensure the element exists
        const elem = this.elementRef.nativeElement;
        if (elem) {
            const attributes = elem.attributes;
            for (let i = 0; i < attributes.length; i++) {
                const attr = attributes.item(i);
                if (attr && attr.name.indexOf('_ngcontent') !== -1) {
                    this.parentStyleAttribute = attr.name;
                }
            }
        }
    }
    ngOnDestroy() {
        this.destroyed = true;
        this._componentRefs = [];
    }
    ngOnChanges(changes) {
        if (changes.rendering || changes.renderings) {
            this._render();
        }
    }
    ngDoCheck() {
        if (!this._differ || !this._inputs || this._componentRefs.length === 0) {
            return;
        }
        const changes = this._differ.diff(this._inputs);
        if (!changes) {
            return;
        }
        const updates = {};
        changes.forEachRemovedItem((change) => (updates[change.key] = null));
        changes.forEachAddedItem((change) => (updates[change.key] = change.currentValue));
        changes.forEachChangedItem((change) => (updates[change.key] = change.currentValue));
        this._componentRefs.forEach((componentRef) => this._setComponentInputs(componentRef, updates));
    }
    _setComponentInputs(componentRef, inputs) {
        Object.entries(inputs).forEach(([input, inputValue]) => componentRef.setInput(input, inputValue));
    }
    _subscribeComponentOutputs(componentInstance, outputs) {
        Object.keys(outputs)
            .filter((output) => componentInstance[output] && componentInstance[output] instanceof Observable)
            .forEach((output) => componentInstance[output]
            .pipe(takeWhile(() => !this.destroyed))
            .subscribe(outputs[output]));
    }
    async _render() {
        if (this.clientOnly && isPlatformServer(this.platformId)) {
            return;
        }
        this._componentRefs = [];
        this.view.clear();
        if (!this.rendering && !this.renderings) {
            return;
        }
        if (!this.name && !this.renderings) {
            console.warn('Placeholder name was not specified, and explicit renderings array was not passed. Placeholder requires either name and rendering, or renderings.');
            this.isLoading = false;
            return;
        }
        const placeholder = this.renderings || getPlaceholder(this.rendering, this.name || '');
        if (!placeholder) {
            console.warn(`Placeholder '${this.name}' was not found in the current rendering data`, JSON.stringify(this.rendering, null, 2));
            this.isLoading = false;
            return;
        }
        // if the placeholder is empty (contains only raw renderings), then we may need to use the empty template if it's defined
        const placeholderIsEmpty = placeholder.every((rendering) => isRawRendering(rendering));
        if (this.renderEmptyTemplate && placeholderIsEmpty) {
            this.view.createEmbeddedView(this.renderEmptyTemplate.templateRef, {
                renderings: placeholder,
            });
            this.isLoading = false;
        }
        else {
            const factories = await this.componentFactory.getComponents(placeholder);
            try {
                const nonGuarded = await this.guardResolver(factories);
                const withData = await this.dataResolver(nonGuarded);
                withData.forEach((rendering, index) => {
                    if (this.renderEachTemplate && !isRawRendering(rendering.factory.componentDefinition)) {
                        this._renderTemplatedComponent(rendering.factory.componentDefinition, index);
                    }
                    else {
                        this._renderEmbeddedComponent(rendering.factory, rendering.data, index);
                    }
                });
                this.isLoading = false;
                this.changeDetectorRef.markForCheck();
                this.loaded.emit(this.name);
            }
            catch (e) {
                this.isLoading = false;
                if (e instanceof JssCanActivateRedirectError) {
                    const redirectValue = e.redirectValue;
                    if (redirectValue instanceof UrlTree) {
                        this.router.navigateByUrl(redirectValue);
                    }
                    else if (typeof redirectValue === 'string') {
                        this.router.navigate([redirectValue]);
                    }
                    else {
                        this.router.navigate(redirectValue);
                    }
                }
                else {
                    this.failed.emit(e);
                    console.warn(`Placeholder '${this.name}' was not able to render with the current rendering data and error`, JSON.stringify(this.rendering, null, 2), e);
                    return;
                }
            }
        }
    }
    _renderTemplatedComponent(rendering, index) {
        // the render-each template takes care of all component mapping etc
        // generally using <sc-render-component> which is about like _renderEmbeddedComponent()
        // as a separate component
        this.view.createEmbeddedView(this.renderEachTemplate.templateRef, {
            rendering,
            index,
        });
    }
    _renderEmbeddedComponent(rendering, data, index) {
        if (rendering.componentDefinition.componentName === HIDDEN_RENDERING_NAME) {
            rendering.componentImplementation = this.hiddenRenderingComponent;
        }
        if (!rendering.componentImplementation) {
            const componentName = rendering.componentDefinition.componentName;
            console.error(`Placeholder ${this.name} contains unknown component ${componentName}.`, `Ensure component is mapped, like:
        JssModule.withComponents([
          { name: '${componentName}', type: ${componentName}Component }
        ])`);
            rendering.componentImplementation = this.missingComponentComponent;
        }
        // apply the parent style attribute _ngcontent
        // work-around for https://github.com/angular/angular/issues/12215
        const createdComponentRef = this.view.createComponent(rendering.componentImplementation, {
            index: index,
            ngModuleRef: rendering.componentModuleRef,
        });
        if (this.parentStyleAttribute) {
            this.renderer.setAttribute(createdComponentRef.location.nativeElement, this.parentStyleAttribute, '');
        }
        const componentInstance = createdComponentRef.instance;
        createdComponentRef.setInput('rendering', rendering.componentDefinition);
        if (Object.keys(data).length > 0) {
            createdComponentRef.setInput('data', data);
        }
        if (this._inputs) {
            this._setComponentInputs(createdComponentRef, this._inputs);
        }
        if (this.outputs) {
            this._subscribeComponentOutputs(componentInstance, this.outputs);
        }
        this._componentRefs.push(createdComponentRef);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PlaceholderComponent, deps: [{ token: i0.KeyValueDiffers }, { token: i1.JssComponentFactoryService }, { token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i2.Router }, { token: PLACEHOLDER_MISSING_COMPONENT_COMPONENT }, { token: PLACEHOLDER_HIDDEN_RENDERING_COMPONENT }, { token: GUARD_RESOLVER }, { token: DATA_RESOLVER }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: PlaceholderComponent, selector: "sc-placeholder,[sc-placeholder]", inputs: { name: "name", rendering: "rendering", renderings: "renderings", outputs: "outputs", clientOnly: "clientOnly", inputs: "inputs" }, outputs: { loaded: "loaded", failed: "failed" }, queries: [{ propertyName: "renderEachTemplate", first: true, predicate: RenderEachDirective, descendants: true, static: true }, { propertyName: "renderEmptyTemplate", first: true, predicate: RenderEmptyDirective, descendants: true, static: true }, { propertyName: "placeholderLoading", first: true, predicate: PlaceholderLoadingDirective, descendants: true, static: true }], viewQueries: [{ propertyName: "view", first: true, predicate: ["view"], descendants: true, read: ViewContainerRef, static: true }], usesOnChanges: true, ngImport: i0, template: `
    <ng-template
      *ngIf="isLoading"
      [ngTemplateOutlet]="placeholderLoading?.templateRef"
    ></ng-template>
    <ng-template #view></ng-template>
  `, isInline: true, dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PlaceholderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sc-placeholder,[sc-placeholder]',
                    template: `
    <ng-template
      *ngIf="isLoading"
      [ngTemplateOutlet]="placeholderLoading?.templateRef"
    ></ng-template>
    <ng-template #view></ng-template>
  `,
                }]
        }], ctorParameters: () => [{ type: i0.KeyValueDiffers }, { type: i1.JssComponentFactoryService }, { type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i2.Router }, { type: i0.Type, decorators: [{
                    type: Inject,
                    args: [PLACEHOLDER_MISSING_COMPONENT_COMPONENT]
                }] }, { type: i0.Type, decorators: [{
                    type: Inject,
                    args: [PLACEHOLDER_HIDDEN_RENDERING_COMPONENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [GUARD_RESOLVER]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DATA_RESOLVER]
                }] }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }], propDecorators: { name: [{
                type: Input
            }], rendering: [{
                type: Input
            }], renderings: [{
                type: Input
            }], outputs: [{
                type: Input
            }], clientOnly: [{
                type: Input
            }], loaded: [{
                type: Output
            }], failed: [{
                type: Output
            }], renderEachTemplate: [{
                type: ContentChild,
                args: [RenderEachDirective, { static: true }]
            }], renderEmptyTemplate: [{
                type: ContentChild,
                args: [RenderEmptyDirective, { static: true }]
            }], placeholderLoading: [{
                type: ContentChild,
                args: [PlaceholderLoadingDirective, { static: true }]
            }], view: [{
                type: ViewChild,
                args: ['view', { read: ViewContainerRef, static: true }]
            }], inputs: [{
                type: Input
            }] } });
